import {
  FilterRangeField,
  FilterRangeInput as FilterRangeInputBox,
} from '@vs-shop/commerce-ui/filterRange';
import { bigDecimalToNumberSafe } from '@vs-shop/domain/price/bigDecimal';
import { BigDecimal, Option, pipe } from 'effect';
import { useMemo } from 'react';
import type { ReactNode } from 'react';

import { FilterRangeNormalizedConfig } from '../../../config/filterSlider';

type Props = {
  value: BigDecimal.BigDecimal;
  min: BigDecimal.BigDecimal;
  max: BigDecimal.BigDecimal;
  children: ReactNode;
  step: BigDecimal.BigDecimal;
  onChange: (value: BigDecimal.BigDecimal) => void;
};

/**
 * One end of a range filter, in rcprofi's terms.
 *
 * The shared box speaks strings and plain numbers; the money and attribute
 * precision this filter runs on is `BigDecimal`, and converting between the two
 * is what stays here. That means: formatting the committed value to as many
 * decimals as the step carries, parsing back whatever was typed, snapping it to
 * the step and clamping it to the facet's bounds.
 */
export default function FilterRangeInput({ value, min, max, children, step, onChange }: Props) {
  // For step < 1 (e.g. 0.01) show decimal places; for step >= 1, integers only.
  const displayValue = useMemo(() => {
    const stepNum = Option.getOrElse(bigDecimalToNumberSafe(step), () => 1);
    const decimalPlaces = stepNum < 1 ? Math.abs(Math.floor(Math.log10(stepNum))) : 0;

    const valueNum = Option.getOrElse(bigDecimalToNumberSafe(value), () => 0);
    return valueNum.toFixed(decimalPlaces);
  }, [value, step]);

  const handleCommit = (typed: string) => {
    const committed = pipe(
      BigDecimal.fromString(typed),
      // Round to step precision: step=1 rounds to integers, step=0.01 to cents.
      Option.flatMap((parsed) => BigDecimal.divide(parsed, step)),
      Option.map((divided) =>
        BigDecimal.clamp(BigDecimal.multiply(BigDecimal.round(divided), step), {
          minimum: min,
          maximum: max,
        })
      )
    );

    // Anything unparseable — an emptied box, a lone minus sign — leaves the
    // filter where it was rather than snapping it to a bound.
    if (Option.isSome(committed)) {
      onChange(committed.value);
    }
  };

  // The native bounds the box advertises. Fall back to the normalized scale so
  // a value that cannot be represented as a number still yields a usable field.
  const minNum = Option.getOrElse(
    bigDecimalToNumberSafe(min),
    () => FilterRangeNormalizedConfig.MIN
  );
  const maxNum = Option.getOrElse(
    bigDecimalToNumberSafe(max),
    () => FilterRangeNormalizedConfig.MAX
  );
  const stepNum = Option.getOrElse(
    bigDecimalToNumberSafe(step),
    () => FilterRangeNormalizedConfig.STEP
  );

  return (
    <FilterRangeField>
      <FilterRangeInputBox
        value={displayValue}
        min={minNum}
        max={maxNum}
        step={stepNum}
        onValueCommit={handleCommit}
      />
      {children}
    </FilterRangeField>
  );
}
